import * as React from 'react';

import { DataStorage } from '../../../../helpers/utils/DataStorage';
import { FileUploadComponent } from './FileUploadComponent';
import { FolderFilesActions } from './FolderFilesActions';
import { ModelAction } from '../../../../types/responses/ModelAction';
import { ProjectDoc } from '../../models/ProjectDoc';
import { services } from '../../../../services';
import { useBackgroundPromiseWrapper } from '../../../../helpers/hooks/useBackgroundPromiseWrapper';
import { NoteUpdatesController } from '../../../notes/helpers/noteUpdates';

interface FolderFilesProps {
    projectDocFolderId: number;
    docTypeId?: number;
    folderTypeId?: number;
    contractorName?: string;
    onCurrentFolderStatusUpdated: () => void;
    dataStorage: DataStorage;
    setDataStorageValue: (key: string, value: any) => void;
    uploadTypeId?: number;
    typeIdFilter?: number;
    readonly?: boolean;
    fetchFiles?: (folderId: number) => Promise<any>;
    hideFolderActions?: boolean;
    maxFiles?: number;
    // Нужны, чтобы из списка файлов можно было открыть документ
    // с замечаниями и посчитать права контрагента.
    projectDocFolderStatusId?: number;
    projectDoc?: ProjectDoc;
    noteUpdates?: NoteUpdatesController;
    accept?: string;
    onInvalidFiles?: () => void;
    onDeleted?: () => void;
    folderCypher?: string;
    folderDocumentOrganization?: string;
}

export const FolderFiles: React.FC<FolderFilesProps> = ({
    projectDocFolderId,
    docTypeId,
    folderTypeId,
    contractorName,
    onCurrentFolderStatusUpdated,
    dataStorage,
    setDataStorageValue,
    uploadTypeId,
    typeIdFilter,
    readonly,
    fetchFiles,
    hideFolderActions,
    maxFiles,
    projectDocFolderStatusId,
    projectDoc,
    noteUpdates,
    accept,
    onInvalidFiles,
    onDeleted,
    folderCypher,
    folderDocumentOrganization
}) => {
    const [folderAttachmentsCount, setFolderAttachmentsCount] = React.useState<number>(0);

    const [actions, setActions] = React.useState<ModelAction[]>([]);

    const [promiseWrapper] = useBackgroundPromiseWrapper();
    const dataStorageRef = React.useRef(dataStorage);
    dataStorageRef.current = dataStorage;

    const actionsRequestIdRef = React.useRef(0);
    const latestFolderIdRef = React.useRef(projectDocFolderId);

    React.useEffect(() => {
        latestFolderIdRef.current = projectDocFolderId;
    }, [projectDocFolderId]);

    const loadActions = React.useCallback(
        (force: boolean = false) => {
            const folderId = projectDocFolderId;
            const ds = dataStorageRef.current;
            if (folderId === 0) {
                setActions([]);
                return;
            }

            const actionsKeyForFolder = `FolderFilesActions_actions_${folderId}`;
            if (!force && ds.hasValue(actionsKeyForFolder)) {
                setActions(ds.get<ModelAction[]>(actionsKeyForFolder, []));
                return;
            }

            const requestId = ++actionsRequestIdRef.current;
            promiseWrapper(
                () => services.projectDocFolderService.actions(folderId),
                (res: ModelAction[]) => {
                    if (requestId !== actionsRequestIdRef.current) {
                        return;
                    }
                    if (latestFolderIdRef.current !== folderId) {
                        return;
                    }
                    ds.set(actionsKeyForFolder, res);
                    setActions(res);
                }
            );
        },
        [projectDocFolderId]
    );

    React.useEffect(() => {
        loadActions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectDocFolderId]);

    const onCurrentFolderStatusUpdatedHandler = React.useCallback(() => {
        onCurrentFolderStatusUpdated();
        loadActions(true);
    }, [loadActions, onCurrentFolderStatusUpdated]);

    return (
        <React.Fragment>
            {!readonly && !hideFolderActions && (
                <div className="row">
                    <div className="col-12 mt-3">
                        <FolderFilesActions
                            actions={actions}
                            projectDocFolderId={projectDocFolderId}
                            filesCount={folderAttachmentsCount}
                            onCurrentFolderStatusUpdated={onCurrentFolderStatusUpdatedHandler}
                            onDeleted={onDeleted}
                        />
                    </div>
                </div>
            )}
            <FileUploadComponent
                actions={actions}
                projectDocFolderId={projectDocFolderId}
                docTypeId={docTypeId}
                folderTypeId={folderTypeId}
                contractorName={contractorName}
                onFilesLoaded={(e) => setFolderAttachmentsCount(e)}
                dataStorage={dataStorage}
                setDataStorageValue={setDataStorageValue}
                uploadTypeId={uploadTypeId}
                typeIdFilter={typeIdFilter}
                readonly={readonly}
                fetchFiles={fetchFiles}
                maxFiles={maxFiles}
                onFilesChanged={onCurrentFolderStatusUpdated}
                projectDocFolderStatusId={projectDocFolderStatusId}
                projectDoc={projectDoc}
                accept={accept}
                onInvalidFiles={onInvalidFiles}
                folderCypher={folderCypher}
                folderDocumentOrganization={folderDocumentOrganization}
                {...(noteUpdates ? { noteUpdates } : {})}
            />
        </React.Fragment>
    );
};
